import { useEffect, useState } from "react";
import { Award, ShieldCheck, Star, Sparkles, Wallet, ExternalLink, Check, HelpCircle, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";

const BAND_STYLES = {
  gold:       { c: "bg-[hsl(var(--sb-gold))]/15 text-[hsl(var(--sb-gold-2))] border border-[hsl(var(--sb-gold))]/40", label: "Gold" },
  silver:     { c: "bg-[hsl(var(--sb-cream))]/70 text-[hsl(150_32%_14%)] border border-[hsl(150_20%_30%)]/30", label: "Silver" },
  bronze:     { c: "bg-[hsl(var(--sb-clay))]/12 text-[hsl(var(--sb-clay-2))] border border-[hsl(var(--sb-clay))]/40", label: "Bronze" },
  developing: { c: "bg-muted text-muted-foreground", label: "Developing" },
  new:        { c: "bg-[hsl(var(--sb-moss))] text-[hsl(40_60%_96%)]", label: "New reader" },
};

const CERT_GENRES = [
  "Horror",
  "Romance",
  "Fantasy",
  "Sci-Fi",
  "Mystery/Thriller",
  "Literary Fiction",
  "Young Adult",
  "Nonfiction",
  "Other",
];
const MANUSCRIPT_OPTIONS = [
  { value: "none", label: "None yet" },
  { value: "1-5", label: "1–5" },
  { value: "6-20", label: "6–20" },
  { value: "20+", label: "20+" },
];

export default function CommunityPage() {
  const { user } = useAuth();
  const isReader = user?.roles?.includes("beta_reader");

  const [score, setScore] = useState(null);
  const [cert, setCert] = useState(null);
  const [statement, setStatement] = useState("");
  const [portfolio, setPortfolio] = useState("");
  const [motivation, setMotivation] = useState("");
  const [genres, setGenres] = useState([]);
  const [manuscriptsRead, setManuscriptsRead] = useState("");
  const [sampleLink, setSampleLink] = useState("");
  const [sampleText, setSampleText] = useState("");
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [respondingInfo, setRespondingInfo] = useState(false);
  const [payout, setPayout] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const load = async () => {
    if (!user?.id) return;
    try {
      const calls = [
        api.get(`/quality/${user.id}`),
        api.get(`/certifications/mine`),
      ];
      if (isReader) calls.push(api.get(`/payments/connect/status`).catch(() => ({ data: null })));
      const results = await Promise.all(calls);
      setScore(results[0].data);
      setCert(results[1].data);
      if (isReader && results[2]) setPayout(results[2].data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id]);

  const apply = async (e) => {
    e.preventDefault();
    if (!agreementAccepted) {
      toast.error("Please accept the community standards agreement to continue.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/certifications/apply`, {
        statement,
        portfolio_notes: portfolio,
        motivation,
        genres,
        manuscripts_read: manuscriptsRead || null,
        sample_link: sampleLink,
        sample_text: sampleText,
        agreement_accepted: agreementAccepted,
      });
      setCert(data);
      toast.success("Application submitted — we review certifications weekly.");
      setStatement("");
      setPortfolio("");
      setMotivation("");
      setGenres([]);
      setManuscriptsRead("");
      setSampleLink("");
      setSampleText("");
      setAgreementAccepted(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const respondToInfoRequest = async ({ message, sample_link }) => {
    setRespondingInfo(true);
    try {
      const { data } = await api.post(`/certifications/respond-info`, {
        message,
        sample_link: sample_link || "",
      });
      setCert(data);
      toast.success("Reply sent — your application is back in the review queue.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRespondingInfo(false);
    }
  };

  const onboardStripe = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/payments/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setConnecting(false);
    }
  };

  return (
    <SiteShell>
      <section className="container-page py-14 space-y-8" data-testid={TID.certifications.root}>
        <div>
          <span className="chip"><Sparkles size={14} /> Reputation</span>
          <h1 className="mt-3 font-serif text-4xl tracking-tight">Quality & Certifications</h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            Your Quality Score reflects how authors experience working with you across Last Bookmark. Certified readers get a badge on their profile and appear first in relevant search.
          </p>
        </div>

        <QualityCard score={score} />

        {isReader && (
          <PayoutCard payout={payout} onConnect={onboardStripe} connecting={connecting} />
        )}

        {isReader ? (
          <CertificationCard
            cert={cert}
            statement={statement}
            setStatement={setStatement}
            portfolio={portfolio}
            setPortfolio={setPortfolio}
            motivation={motivation}
            setMotivation={setMotivation}
            genres={genres}
            setGenres={setGenres}
            manuscriptsRead={manuscriptsRead}
            setManuscriptsRead={setManuscriptsRead}
            sampleLink={sampleLink}
            setSampleLink={setSampleLink}
            sampleText={sampleText}
            setSampleText={setSampleText}
            agreementAccepted={agreementAccepted}
            setAgreementAccepted={setAgreementAccepted}
            onSubmit={apply}
            saving={saving}
            onRespondInfo={respondToInfoRequest}
            respondingInfo={respondingInfo}
          />
        ) : (
          <div className="card-soft">
            <h2 className="font-serif text-2xl flex items-center gap-2"><Award size={20} /> Last Bookmark Certified</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Certification is available for readers. Add the Beta Reader role to your profile to apply.
            </p>
          </div>
        )}
      </section>
    </SiteShell>
  );
}

function PayoutCard({ payout, onConnect, connecting }) {
  const ready = payout?.payouts_enabled;
  const demo = payout?.demo_mode;
  const onboarded = payout?.details_submitted;
  return (
    <div className="card-soft" data-testid={TID.stripeConnect.card}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl flex items-center gap-2"><Wallet size={20} /> Payouts (preview)</h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            {demo
              ? "Integrated payouts are not currently active during beta. Stripe Connect onboarding runs in demo mode here so we can preview the flow — your details are recorded but no real payouts happen yet. Real payouts will activate in a future update."
              : ready
                ? "Your Stripe Express account is connected. Integrated payouts are still being finalised and will activate in a future update — no real payouts happen during beta."
                : payout?.connected
                  ? "You've started the preview onboarding. You can complete the remaining steps to test the flow; real payouts are not yet available during beta."
                  : "Preview Stripe Express onboarding so you can see how payouts will work. Integrated payouts are planned for a future update and are not currently active during beta."}
          </p>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <span className={`text-[10px] uppercase tracking-[0.18em] rounded-full px-3 py-1 ${
            ready ? "bg-[hsl(var(--sb-moss))] text-[hsl(var(--sb-forest-2))]" : "bg-muted text-muted-foreground"
          }`}>{ready ? "Preview connected" : onboarded ? "Preview onboarding complete" : payout?.connected ? "Preview onboarding in progress" : "Not connected"}</span>
          {demo && (
            <span data-testid={TID.stripeConnect.demoBadge}
              className="text-[10px] uppercase tracking-[0.18em] rounded-full px-3 py-1 bg-[hsl(var(--sb-clay))]/15 text-[hsl(var(--sb-clay-2))]">
              Not active in beta
            </span>
          )}
        </div>
      </div>
      {!ready && (
        <button onClick={onConnect} disabled={connecting} className="btn-primary mt-4" data-testid={TID.stripeConnect.onboard}>
          {connecting ? "Redirecting…" : onboarded ? "Re-run preview onboarding" : payout?.connected ? "Continue preview onboarding" : "Preview Stripe onboarding"}
          <ExternalLink size={14} />
        </button>
      )}
    </div>
  );
}

function QualityCard({ score }) {
  const band = BAND_STYLES[score?.band || "new"];
  return (
    <div className="card-soft">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl flex items-center gap-2"><ShieldCheck size={20} /> Quality Score</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {score?.reviews_count === 0
              ? "No reviews yet — complete a project to earn your first score."
              : "Weighted from your reviews and completion history."}
          </p>
        </div>
        <span className={`text-xs uppercase tracking-[0.18em] rounded-full px-3 py-1 ${band.c}`}>{band.label}</span>
      </div>
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Score" value={score?.score != null ? `${score.score}` : "—"} big />
        <Stat label="Reviews" value={score?.reviews_count ?? 0} />
        <Stat label="Avg rating" value={score?.avg != null ? `${score.avg} / 5` : "—"} />
        <Stat label="Reliability" value={score?.reliability != null ? `${Math.round((score.reliability || 0) * 100)}%` : "—"} />
      </div>
    </div>
  );
}

function CertificationCard({
  cert,
  statement,
  setStatement,
  portfolio,
  setPortfolio,
  motivation,
  setMotivation,
  genres,
  setGenres,
  manuscriptsRead,
  setManuscriptsRead,
  sampleLink,
  setSampleLink,
  sampleText,
  setSampleText,
  agreementAccepted,
  setAgreementAccepted,
  onSubmit,
  saving,
  onRespondInfo,
  respondingInfo,
}) {
  if (cert?.status === "granted") {
    return (
      <div className="card-soft">
        <div className="flex items-center gap-3">
          <Award size={22} className="text-[hsl(var(--sb-clay))]" />
          <h2 className="font-serif text-2xl">You are Last Bookmark Certified</h2>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          Authors browsing the marketplace will see the Certified badge on your profile.
          Certification is a Last Bookmark platform designation based on your application review;
          keep providing thoughtful, kind, thorough feedback to keep it.
        </p>
      </div>
    );
  }
  if (cert?.status === "more_info_requested") {
    return (
      <MoreInfoResponsePanel cert={cert} onRespond={onRespondInfo} busy={respondingInfo} />
    );
  }
  if (cert?.status === "pending") {
    return (
      <div className="card-soft">
        <h2 className="font-serif text-2xl">Application received</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          A reviewer will look at your application within the next few days. You&apos;ll be notified
          when the decision is made, or if we need more information from you.
        </p>
      </div>
    );
  }
  const toggleGenre = (g) => {
    setGenres((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));
  };
  const trimmedText = (sampleText || "").trim();
  const sampleProvided = Boolean((sampleLink || "").trim()) || trimmedText.length >= 300;
  const canSubmit = agreementAccepted && sampleProvided && !saving;
  return (
    <form onSubmit={onSubmit} className="card-soft" data-testid={TID.certifications.apply}>
      <h2 className="font-serif text-2xl flex items-center gap-2"><Award size={20} /> Apply for Last Bookmark Certified</h2>
      <div className="mt-3 rounded-xl border border-[hsl(var(--sb-forest))]/25 bg-[hsl(var(--sb-moss))]/40 p-3.5 text-xs text-foreground leading-relaxed">
        <span className="font-medium">What this means: </span>
        Certification is a manual review — we look at your experience, this application, and a
        sample or portfolio showing the quality of your feedback. It&apos;s a Last Bookmark platform
        designation, not an industry credential, and it doesn&apos;t guarantee your work. Last
        Bookmark also doesn&apos;t independently verify every claim you make below.
      </div>
      <p className="mt-3 text-sm text-muted-foreground">
        Tell us about your beta reading experience and how you deliver thoughtful, kind, thorough feedback.
      </p>
      <div className="mt-5 space-y-5">
        <div>
          <label className="text-sm font-medium mb-1.5 block">
            Your statement <span className="text-destructive">*</span>
          </label>
          <textarea
            rows={6}
            required
            minLength={50}
            className="sb-input"
            data-testid={TID.certifications.statement}
            placeholder="Books you've read, feedback style, community standards you endorse…"
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="cert-motivation" className="text-sm font-medium mb-1.5 block">
            Why do you want to become a Certified Beta Reader? <span className="text-destructive">*</span>
          </label>
          <textarea
            id="cert-motivation"
            rows={4}
            required
            minLength={20}
            className="sb-input"
            placeholder="What draws you to this? What kind of impact do you hope to have on the authors you work with?"
            value={motivation}
            onChange={(e) => setMotivation(e.target.value)}
            data-testid="cert-motivation"
          />
        </div>

        <fieldset>
          <legend className="text-sm font-medium mb-2">Genres you specialize in <span className="text-muted-foreground font-normal">(optional)</span></legend>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="cert-genres-group">
            {CERT_GENRES.map((g) => {
              const checked = genres.includes(g);
              return (
                <label
                  key={g}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm cursor-pointer transition-colors ${
                    checked
                      ? "border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-moss))]/60 text-[hsl(var(--sb-forest-2))]"
                      : "hover:bg-muted/60"
                  }`}
                  data-testid={`cert-genre-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border accent-[hsl(var(--sb-forest))] cursor-pointer"
                    checked={checked}
                    onChange={() => toggleGenre(g)}
                  />
                  <span className="truncate">{g}</span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <fieldset>
          <legend className="text-sm font-medium mb-2">
            Approximately how many manuscripts have you beta read? <span className="text-muted-foreground font-normal">(optional)</span>
          </legend>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="cert-manuscripts-group">
            {MANUSCRIPT_OPTIONS.map((opt) => {
              const active = manuscriptsRead === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm cursor-pointer transition-colors ${
                    active
                      ? "border-[hsl(var(--sb-forest))] bg-[hsl(var(--sb-moss))]/60 text-[hsl(var(--sb-forest-2))]"
                      : "hover:bg-muted/60"
                  }`}
                  data-testid={`cert-manuscripts-${opt.value}`}
                >
                  <input
                    type="radio"
                    name="cert-manuscripts"
                    value={opt.value}
                    checked={active}
                    onChange={() => setManuscriptsRead(opt.value)}
                    className="sr-only"
                  />
                  {opt.label}
                </label>
              );
            })}
            {manuscriptsRead && (
              <button
                type="button"
                onClick={() => setManuscriptsRead("")}
                className="text-xs text-muted-foreground underline hover:text-foreground col-span-2 sm:col-span-4 mt-1 justify-self-start"
                data-testid="cert-manuscripts-clear"
              >
                Clear selection
              </button>
            )}
          </div>
        </fieldset>

        <fieldset className="space-y-3" data-testid="cert-sample-section">
          <legend className="text-sm font-medium">
            Feedback sample <span className="text-destructive">*</span>
          </legend>
          <p className="text-xs text-muted-foreground -mt-1">
            Share <span className="font-medium">either</span> a working direct link to your feedback <span className="font-medium">or</span> paste a sample of at least 300 characters below.
          </p>
          <div>
            <label htmlFor="cert-sample-link" className="text-sm font-medium mb-1.5 block">
              Working direct link to a review, doc, or portfolio page
            </label>
            <input
              id="cert-sample-link"
              type="url"
              className="sb-input"
              placeholder="e.g. https://goodreads.com/review/show/…  or  https://docs.google.com/document/d/…"
              value={sampleLink}
              onChange={(e) => setSampleLink(e.target.value)}
              data-testid="cert-sample-link"
              maxLength={500}
            />
            <p className="mt-1.5 text-xs text-muted-foreground">
              Must start with http(s):// and point directly at the page that shows your feedback — homepage-only links can&apos;t be accepted.
            </p>
          </div>
          <div className="text-center text-[11px] uppercase tracking-[0.16em] text-muted-foreground">or</div>
          <div>
            <label htmlFor="cert-sample-text" className="text-sm font-medium mb-1.5 block">
              Paste a feedback sample <span className="text-muted-foreground font-normal">(min 300 characters)</span>
            </label>
            <textarea
              id="cert-sample-text"
              rows={8}
              className="sb-input"
              placeholder="Paste a real excerpt of beta-reading feedback you've given (with the author's permission), or a mini feedback sample on a short excerpt of published prose. Show us how you notice pacing, character, craft, and how you deliver notes."
              value={sampleText}
              onChange={(e) => setSampleText(e.target.value)}
              data-testid="cert-sample-text"
              maxLength={4000}
            />
            <div className="mt-1 flex items-center justify-between text-xs">
              <span
                className={
                  trimmedText.length === 0
                    ? "text-muted-foreground"
                    : trimmedText.length >= 300
                      ? "text-[hsl(var(--sb-forest-2))] font-medium"
                      : "text-[hsl(var(--sb-clay-2))]"
                }
                data-testid="cert-sample-text-counter"
              >
                {trimmedText.length}/300 characters
                {trimmedText.length > 0 && trimmedText.length < 300 && ` — ${300 - trimmedText.length} more to unlock`}
              </span>
              <span className="text-muted-foreground">{trimmedText.length}/4000</span>
            </div>
          </div>
          {!sampleProvided && (agreementAccepted || sampleLink || trimmedText) && (
            <p
              className="rounded-lg border border-[hsl(var(--sb-clay))]/40 bg-[hsl(var(--sb-clay))]/10 p-2.5 text-xs text-[hsl(var(--sb-clay-2))]"
              data-testid="cert-sample-warning"
            >
              Please share a working sample link or paste at least 300 characters of feedback before submitting.
            </p>
          )}
        </fieldset>

        <div>
          <label className="text-sm font-medium mb-1.5 block">Portfolio notes <span className="text-muted-foreground font-normal">(optional)</span></label>
          <textarea
            rows={3}
            className="sb-input"
            placeholder="Additional references reviewers can check."
            value={portfolio}
            onChange={(e) => setPortfolio(e.target.value)}
          />
        </div>

        <label
          className={`flex items-start gap-3 rounded-2xl border p-3.5 cursor-pointer transition-colors ${
            agreementAccepted
              ? "border-[hsl(var(--sb-forest))]/50 bg-[hsl(var(--sb-moss))]/40"
              : "bg-muted/30 hover:bg-muted/50"
          }`}
          data-testid="cert-agreement-wrapper"
        >
          <input
            type="checkbox"
            checked={agreementAccepted}
            onChange={(e) => setAgreementAccepted(e.target.checked)}
            required
            className="mt-0.5 h-4 w-4 rounded border-border accent-[hsl(var(--sb-forest))] cursor-pointer"
            data-testid="cert-agreement-checkbox"
          />
          <span className="text-xs leading-relaxed text-foreground">
            <span className="font-medium">I agree</span> to provide honest, respectful, and constructive feedback, keep authors' work confidential, and follow the Last Bookmark community standards. <span className="text-destructive">*</span>
          </span>
        </label>
      </div>
      <div className="mt-6 flex items-center justify-end gap-3">
        {(!agreementAccepted || !sampleProvided) && (
          <span className="text-xs text-muted-foreground hidden sm:inline">
            {!sampleProvided
              ? "Share a sample link or paste 300+ characters."
              : "Accept the agreement to submit."}
          </span>
        )}
        <button type="submit" className="btn-primary" disabled={!canSubmit} data-testid={TID.certifications.submit}>
          <Check size={14} /> {saving ? "Submitting…" : "Submit application"}
        </button>
      </div>
    </form>
  );
}

function Stat({ label, value, big }) {
  return (
    <div className="rounded-xl border p-3">
      <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
      <div className={`mt-1 font-medium ${big ? "text-3xl font-serif" : "text-sm"}`}>{value}</div>
    </div>
  );
}


function MoreInfoResponsePanel({ cert, onRespond, busy }) {
  const [message, setMessage] = useState("");
  const [sampleLink, setSampleLink] = useState("");
  const [sampleText, setSampleText] = useState("");
  const infoRequests = cert?.info_requests || [];
  const infoResponses = cert?.info_responses || [];
  const latestAsk = infoRequests[infoRequests.length - 1];
  const trimmedText = sampleText.trim();
  const textTooShort = trimmedText.length > 0 && trimmedText.length < 300;
  const canSend =
    message.trim().length >= 10 && !textTooShort && !busy;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    await onRespond({
      message: message.trim(),
      sample_link: sampleLink.trim(),
      sample_text: trimmedText,
    });
    setMessage("");
    setSampleLink("");
    setSampleText("");
  };

  return (
    <div className="card-soft" data-testid="cert-more-info-panel">
      <div className="flex items-center gap-2 text-[hsl(var(--sb-clay-2))]">
        <HelpCircle size={18} />
        <h2 className="font-serif text-2xl">We need a bit more information</h2>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        Your certification application is on hold while we wait to hear back from you. Reply
        below and it&apos;ll go straight back into the review queue — no need to start a new
        application.
      </p>

      {latestAsk && (
        <div
          className="mt-5 rounded-xl border bg-background p-3.5"
          data-testid="cert-more-info-latest"
        >
          <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
            <span>Reviewer message</span>
            <span>
              {latestAsk.requested_at
                ? new Date(latestAsk.requested_at).toLocaleString()
                : ""}
            </span>
          </div>
          <div className="mt-1.5 text-sm whitespace-pre-wrap leading-relaxed">
            {latestAsk.message}
          </div>
        </div>
      )}

      {infoResponses.length > 0 && (
        <div className="mt-4 rounded-xl border bg-muted/30 p-3.5">
          <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground flex items-center gap-1.5">
            <MessagesSquare size={12} /> Your previous replies
          </div>
          <ul className="mt-2 space-y-2 text-sm">
            {infoResponses.map((r, i) => (
              <li key={i} className="rounded-lg border bg-background p-2.5">
                <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                  {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : ""}
                </div>
                <div className="mt-1 whitespace-pre-wrap">{r.message}</div>
                {r.sample_link && (
                  <a
                    href={r.sample_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1.5 inline-flex items-center gap-1 text-xs link-underline text-[hsl(var(--sb-forest-2))] break-all"
                  >
                    {r.sample_link} <ExternalLink size={11} />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-5 space-y-4">
        <div>
          <label htmlFor="cert-info-reply" className="text-sm font-medium mb-1.5 block">
            Your reply <span className="text-destructive">*</span>
          </label>
          <textarea
            id="cert-info-reply"
            rows={5}
            className="sb-input"
            placeholder="Answer what the reviewer asked for. If it's a link problem, share the working link below."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid="cert-info-reply"
            minLength={10}
            maxLength={4000}
            required
          />
          <div className="mt-1 text-[11px] text-muted-foreground">
            {message.trim().length}/4000 · minimum 10 characters.
          </div>
        </div>
        <div>
          <label
            htmlFor="cert-info-sample-link"
            className="text-sm font-medium mb-1.5 block"
          >
            Updated sample or portfolio link <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <input
            id="cert-info-sample-link"
            type="url"
            className="sb-input"
            placeholder="A working direct link to your review, feedback doc, or portfolio page"
            value={sampleLink}
            onChange={(e) => setSampleLink(e.target.value)}
            data-testid="cert-info-sample-link"
            maxLength={500}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Must start with http(s):// and point directly at the content — homepage-only links can&apos;t be accepted.
          </p>
        </div>
        <div>
          <label
            htmlFor="cert-info-sample-text"
            className="text-sm font-medium mb-1.5 block"
          >
            Or paste a feedback sample instead <span className="text-muted-foreground font-normal">(optional, min 300 chars)</span>
          </label>
          <textarea
            id="cert-info-sample-text"
            rows={6}
            className="sb-input"
            placeholder="A real excerpt of beta-reading feedback you've given, or a mini feedback sample on a short excerpt of published prose."
            value={sampleText}
            onChange={(e) => setSampleText(e.target.value)}
            data-testid="cert-info-sample-text"
            maxLength={4000}
          />
          <div className="mt-1 text-[11px] text-muted-foreground">
            {trimmedText.length}/300 · {textTooShort ? `${300 - trimmedText.length} more to unlock` : "leave blank if you don't want to paste one"}
          </div>
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!canSend}
            className="btn-primary"
            data-testid="cert-info-send"
          >
            <Check size={14} /> {busy ? "Sending…" : "Send reply"}
          </button>
        </div>
      </form>
    </div>
  );
}
